import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Search, Plus, Minus, Trash2, Printer, FileText, X, ChevronLeft, Pencil
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import { numberToArabicWords } from '../utils/arabicNumbers';
import QuickAddInventoryDialog from '../components/dialogs/QuickAddInventoryDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FauxInvoicesPage = () => {
  const { user, currentOrg } = useAuth();

  // State
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | create | print
  const [printInvoice, setPrintInvoice] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // Create form
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [customerName, setCustomerName] = useState('');
  const [customerId, setCustomerId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [lines, setLines] = useState([{ description: '', quantity: 1, unit_price: 0, item_id: null, currency: 'LBP', is_taxed: false }]);
  const [currency, setCurrency] = useState('LBP'); // Invoice-level currency
  const [itemSearch, setItemSearch] = useState('');
  const [itemResults, setItemResults] = useState([]);
  const [activeLineIdx, setActiveLineIdx] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const customerRef = useRef(null);
  const [qaItemOpen, setQaItemOpen] = useState(false);
  const [qaItemMode, setQaItemMode] = useState('add');
  const [qaItemEdit, setQaItemEdit] = useState(null);

  // Filters
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  
  // Exchange rate for currency conversion
  const [exchangeRate, setExchangeRate] = useState(89500);

  // Fetch exchange rate
  useEffect(() => {
    if (!currentOrg) return;
    const fetchRate = async () => {
      try {
        const res = await axios.get(`${API}/exchange-rate?organization_id=${currentOrg.id}`);
        if (res.data?.rate) setExchangeRate(res.data.rate);
      } catch (err) {
        console.error('Failed to fetch exchange rate:', err);
      }
    };
    fetchRate();
  }, [currentOrg]);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ organization_id: currentOrg.id });
      if (filterSearch) params.append('search', filterSearch);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      const res = await axios.get(`${API}/faux-invoices?${params}`);
      setInvoices(res.data);
    } catch (err) {
      console.error('Failed to fetch faux invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg, filterSearch, filterDateFrom, filterDateTo]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Customer search
  useEffect(() => {
    if (!customerSearch.trim() || !currentOrg) { setCustomerResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/customers?organization_id=${currentOrg.id}&search=${encodeURIComponent(customerSearch)}`);
        const data = Array.isArray(res.data) ? res.data : (res.data.customers || []);
        setCustomerResults(data.slice(0, 8));
        setShowCustomerDropdown(true);
      } catch { setCustomerResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch, currentOrg]);

  // Item search
  useEffect(() => {
    if (!itemSearch.trim() || !currentOrg) { setItemResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/pos/inventory?organization_id=${currentOrg.id}&search=${encodeURIComponent(itemSearch)}`);
        setItemResults((res.data || []).slice(0, 10));
      } catch { setItemResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [itemSearch, currentOrg]);

  const selectCustomer = (c) => {
    setCustomerName(c.name || c.company_name || '');
    setCustomerId(c.id);
    setCustomerSearch(c.name || c.company_name || '');
    setShowCustomerDropdown(false);
    setCustomerResults([]);
  };

  const selectItem = (item, idx) => {
    const updated = [...lines];
    updated[idx] = {
      ...updated[idx],
      description: item.name,
      unit_price: item.price || 0,
      item_id: item.id,
      currency: item.currency || currency,
      is_taxed: item.is_taxable || false
    };
    setLines(updated);
    setItemSearch('');
    setItemResults([]);
    setActiveLineIdx(null);
  };

  const updateLine = (idx, field, value) => {
    const updated = [...lines];
    updated[idx] = { ...updated[idx], [field]: value };
    setLines(updated);
  };

  const addLine = () => {
    setLines([...lines, { description: '', quantity: 1, unit_price: 0, item_id: null, currency: 'LBP', is_taxed: false }]);
  };

  const removeLine = (idx) => {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, i) => i !== idx));
  };

  // Calculate total in invoice currency (converting mixed currencies)
  const total = lines.reduce((sum, line) => {
    const lineTotal = (line.quantity || 0) * (line.unit_price || 0);
    const lineCurrency = line.currency || 'LBP';
    
    // Convert to invoice currency if different
    if (lineCurrency === currency) {
      return sum + lineTotal;
    } else if (lineCurrency === 'USD' && currency === 'LBP') {
      // USD to LBP
      return sum + (lineTotal * exchangeRate);
    } else if (lineCurrency === 'LBP' && currency === 'USD') {
      // LBP to USD
      return sum + (lineTotal / exchangeRate);
    }
    return sum + lineTotal;
  }, 0);

  const handleEdit = (inv) => {
    setEditingId(inv.id);
    setDate(inv.date);
    setCustomerName(inv.customer_name);
    setCustomerSearch(inv.customer_name);
    setCustomerId(inv.customer_id || null);
    setCurrency(inv.currency || 'LBP');
    setLines(inv.items.map(it => ({
      description: it.description,
      quantity: it.quantity,
      unit_price: it.unit_price,
      item_id: it.item_id || null,
      currency: it.currency || inv.currency || 'LBP',
      is_taxed: it.is_taxed || false
    })));
    setNotes(inv.notes || '');
    setView('create');
  };

  const handleSave = async () => {
    if (!customerName.trim()) { alert('Please enter a customer name'); return; }
    if (lines.every(l => !l.description.trim())) { alert('Add at least one item'); return; }
    if (!currentOrg) return;

    setSaving(true);
    try {
      const payload = {
        date,
        customer_id: customerId,
        customer_name: customerName.trim() || customerSearch.trim(),
        currency: currency,
        items: lines.filter(l => l.description.trim()).map(l => ({
          item_id: l.item_id,
          description: l.description,
          quantity: parseFloat(l.quantity) || 1,
          unit_price: parseFloat(l.unit_price) || 0,
          total_price: (parseFloat(l.quantity) || 1) * (parseFloat(l.unit_price) || 0),
          currency: l.currency || currency,
          is_taxed: l.is_taxed || false
        })),
        notes: notes || null,
        organization_id: currentOrg.id
      };
      let res;
      if (editingId) {
        res = await axios.put(`${API}/faux-invoices/${editingId}`, payload);
      } else {
        res = await axios.post(`${API}/faux-invoices`, payload);
      }
      setPrintInvoice(res.data);
      setView('print');
      resetForm();
      fetchInvoices();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setDate(new Date().toISOString().split('T')[0]);
    setCustomerName('');
    setCustomerId(null);
    setCustomerSearch('');
    setLines([{ description: '', quantity: 1, unit_price: 0, item_id: null, currency: 'LBP', is_taxed: false }]);
    setCurrency('LBP');
    setNotes('');
    setEditingId(null);
  };

  const handlePrint = (inv) => {
    setPrintInvoice(inv);
    setView('print');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this faux invoice?')) return;
    try {
      await axios.delete(`${API}/faux-invoices/${id}`);
      fetchInvoices();
    } catch { alert('Failed to delete'); }
  };

  const doPrint = () => {
    window.print();
  };

  // ===================== PRINT VIEW =====================
  if (view === 'print' && printInvoice) {
    return (
      <div className="min-h-screen bg-white">
        <style>{`
          @media print {
            .no-print, nav, aside, header, [class*="sidebar"], [class*="Sidebar"] { display: none !important; visibility: hidden !important; }
            body { margin: 0; padding: 0; background: white !important; }
            @page { size: A5 portrait; margin: 5mm; }
            .print-page { padding: 2mm !important; margin: 0 !important; overflow: hidden; }
            html, body { height: auto; overflow: hidden; }
          }
        `}</style>

        <div className="no-print bg-slate-100 p-4 flex items-center gap-3 border-b">
          <Button variant="outline" size="sm" onClick={() => setView('list')}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to List
          </Button>
          <Button onClick={doPrint} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="print-faux-btn">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
        </div>

        <div className="print-page mx-auto p-4" style={{ fontFamily: 'Times New Roman, serif', maxWidth: '148mm', fontSize: '14px', fontWeight: 'bold' }}>
          {/* Custom pharmacy header: English text left, Logo center, Arabic text right */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
            {/* English Header - left */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#000' }}>ST. JOHN PHARMACY</div>
              <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#000', marginTop: '2px' }}>Milad G. Mekary R.Ph.</div>
              <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#000', marginTop: '2px' }}>Slaib Street - 06/663197 - 81/450539</div>
            </div>

            {/* Logo - center */}
            <div style={{ width: '70px', flexShrink: 0, textAlign: 'center', padding: '0 8px' }}>
              <img src="/pharmacy-header/logo.png" alt="Pharmacy Logo" style={{ width: '55px', height: 'auto' }} />
            </div>

            {/* Arabic Header - right */}
            <div style={{ flex: 1, textAlign: 'center' }} dir="rtl">
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#000' }}>صيدلية ماريوحنا</div>
              <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#000', marginTop: '2px' }}>الصيدلي ميلاد جرجس المكاري</div>
              <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#000', marginTop: '2px' }}>شارع الصليب - ٦/٦٦٣١٩٧ - ٨١/٤٠٥٣٩</div>
              <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#000', marginTop: '1px' }}>سجل تجاري: ١٣٥٦٤ - الرخصة: ١/١٣٢٢</div>
              <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#000', marginTop: '1px' }}>النقابة: ٢٥٦٤ - الضمان: ٧/١٥٦</div>
            </div>
          </div>

          {/* Serial and Date */}
          <div className="flex justify-between items-center mb-2" style={{ fontSize: '14px', fontWeight: 'bold' }}>
            <div><strong>N= </strong>{printInvoice.serial_number}</div>
            <div><strong>Date / التاريخ: </strong>{printInvoice.date}</div>
          </div>

          {/* Customer - centered above table */}
          <div className="text-center mb-2" style={{ fontSize: '15px', fontWeight: 'bold' }}>
            <span>المطلوب من السيد / Due From: </span>
            <strong>{printInvoice.customer_name}</strong>
          </div>

          {/* Items Table */}
          <table className="w-full border-collapse" style={{ fontSize: '13px', border: '1px solid #999', fontWeight: 'bold' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ border: '1px solid #999', padding: '4px', textAlign: 'center', width: '45px' }}>
                  <div>الكمية</div><div>Qty</div>
                </th>
                <th style={{ border: '1px solid #999', padding: '4px', textAlign: 'center' }}>
                  <div>النوع</div><div>Description</div>
                </th>
                <th style={{ border: '1px solid #999', padding: '4px', textAlign: 'center', width: '75px' }}>
                  <div>السعر الإفرادي</div><div>Unit Price</div>
                </th>
                <th style={{ border: '1px solid #999', padding: '4px', textAlign: 'center', width: '75px' }}>
                  <div>السعر الإجمالي</div><div>TOTAL</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {printInvoice.items.map((item, idx) => {
                const curr = item.currency || printInvoice.currency || 'LBP';
                const isLBP = curr === 'LBP';
                const formatPrice = (val) => {
                  const num = Number(val) || 0;
                  if (isLBP) {
                    return Math.round(num).toLocaleString() + ' LBP';
                  }
                  return num.toFixed(2) + ' USD';
                };
                return (
                  <tr key={idx}>
                    <td style={{ border: '1px solid #999', padding: '4px', textAlign: 'center' }}>{item.quantity}</td>
                    <td style={{ border: '1px solid #999', padding: '4px' }}>
                      {item.is_taxed && <span style={{ color: '#c00' }}>* </span>}
                      {item.description}
                    </td>
                    <td style={{ border: '1px solid #999', padding: '4px', textAlign: 'right', fontFamily: 'monospace' }}>{formatPrice(item.unit_price)}</td>
                    <td style={{ border: '1px solid #999', padding: '4px', textAlign: 'right', fontFamily: 'monospace' }}>{formatPrice(item.total_price)}</td>
                  </tr>
                );
              })}
              {Array.from({ length: Math.max(0, 6 - printInvoice.items.length) }).map((_, i) => (
                <tr key={`e-${i}`}>
                  <td style={{ border: '1px solid #999', padding: '4px' }}>&nbsp;</td>
                  <td style={{ border: '1px solid #999', padding: '4px' }}>&nbsp;</td>
                  <td style={{ border: '1px solid #999', padding: '4px' }}>&nbsp;</td>
                  <td style={{ border: '1px solid #999', padding: '4px' }}>&nbsp;</td>
                </tr>
              ))}
              {(() => {
                const invoiceCurrency = printInvoice.currency || 'LBP';
                const isLBP = invoiceCurrency === 'LBP';
                const hasTaxedItems = printInvoice.items.some(it => it.is_taxed);
                
                // Recalculate total with currency conversion
                const calculatedTotal = printInvoice.items.reduce((sum, item) => {
                  const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
                  const lineCurrency = item.currency || 'LBP';
                  
                  if (lineCurrency === invoiceCurrency) {
                    return sum + lineTotal;
                  } else if (lineCurrency === 'USD' && invoiceCurrency === 'LBP') {
                    return sum + (lineTotal * exchangeRate);
                  } else if (lineCurrency === 'LBP' && invoiceCurrency === 'USD') {
                    return sum + (lineTotal / exchangeRate);
                  }
                  return sum + lineTotal;
                }, 0);
                
                const totalFormatted = isLBP 
                  ? Math.round(calculatedTotal).toLocaleString() + ' LBP'
                  : calculatedTotal.toFixed(2) + ' USD';
                return (
                  <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    <td colSpan="3" style={{ border: '1px solid #999', padding: '5px', textAlign: 'right' }}>
                      {hasTaxedItems && <span style={{ color: '#c00' }}>* </span>}
                      المجموع / TTC
                    </td>
                    <td style={{ border: '1px solid #999', padding: '5px', textAlign: 'right', fontFamily: 'monospace' }}>
                      {totalFormatted}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>

          {/* Arabic number-to-words with Only/فقط */}
          {(() => {
            const invoiceCurrency = printInvoice.currency || 'LBP';
            // Recalculate total with currency conversion for numword
            const calculatedTotal = printInvoice.items.reduce((sum, item) => {
              const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
              const lineCurrency = item.currency || 'LBP';
              
              if (lineCurrency === invoiceCurrency) {
                return sum + lineTotal;
              } else if (lineCurrency === 'USD' && invoiceCurrency === 'LBP') {
                return sum + (lineTotal * exchangeRate);
              } else if (lineCurrency === 'LBP' && invoiceCurrency === 'USD') {
                return sum + (lineTotal / exchangeRate);
              }
              return sum + lineTotal;
            }, 0);
            
            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                <span style={{ fontStyle: 'italic' }}>Only</span>
                <span style={{ flex: 1, borderBottom: '1px solid #333', margin: '0 8px', textAlign: 'center', fontWeight: 'bold', paddingBottom: '2px', fontSize: '13px' }} dir="rtl">
                  {numberToArabicWords(invoiceCurrency === 'LBP' ? Math.round(calculatedTotal) : calculatedTotal, invoiceCurrency)}
                </span>
                <span style={{ fontWeight: 'bold' }} dir="rtl">فقط</span>
              </div>
            );
          })()}

          {/* Footer - Signature & Payment */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', fontSize: '15px', fontWeight: 'bold' }}>
            <div style={{ textAlign: 'left' }}>
              <div dir="rtl" style={{ fontWeight: 'bold', marginBottom: '4px' }}>الختم والتوقيع</div>
              <div style={{ fontWeight: 'bold' }}>SIG. & Stamp</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div dir="rtl" style={{ fontWeight: 'bold', marginBottom: '4px' }}>القيمة وصلتنا نقداً</div>
              <div style={{ fontWeight: 'bold' }}>Paid Cash</div>
            </div>
          </div>

          {printInvoice.notes && (
            <div style={{ fontSize: '12px', marginTop: '12px', fontWeight: 'bold' }}>
              <strong>Notes: </strong>{printInvoice.notes}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===================== CREATE VIEW =====================
  if (view === 'create') {
    return (
      <div className="p-4 lg:p-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={() => { setView('list'); resetForm(); }}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-xl font-bold" data-testid="faux-create-title">{editingId ? 'Edit Faux Invoice' : 'New Faux Invoice'}</h1>
          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">DRAFT - No financial impact</span>
        </div>

        <div className="space-y-4">
          {/* Date & Customer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="faux-date" />
            </div>

            <div className="space-y-1 relative" ref={customerRef}>
              <Label>Customer (Due From / المطلوب من السيد)</Label>
              <Input
                value={customerSearch || customerName}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setCustomerName(e.target.value);
                  setCustomerId(null);
                  if (!e.target.value.trim()) setShowCustomerDropdown(false);
                }}
                onFocus={() => { if (customerResults.length) setShowCustomerDropdown(true); }}
                onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                placeholder="Search or type customer name..."
                data-testid="faux-customer-input"
              />
              {showCustomerDropdown && customerResults.length > 0 && (
                <div className="absolute z-50 w-full bg-background border rounded-md shadow-lg max-h-48 overflow-auto top-full mt-1">
                  {customerResults.map(c => (
                    <div key={c.id} className="px-3 py-2 hover:bg-muted cursor-pointer text-sm"
                      onMouseDown={() => selectCustomer(c)}>
                      {c.name || c.company_name}
                      {c.phone && <span className="text-muted-foreground ml-2">({c.phone})</span>}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-0.5">Select from customers or just type a name</p>
            </div>

            {/* Total Display Currency */}
            <div className="space-y-1">
              <Label>Total Currency (for display)</Label>
              <select 
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                data-testid="faux-invoice-currency"
              >
                <option value="LBP">LBP (Lebanese Pound)</option>
                <option value="USD">USD (US Dollar)</option>
              </select>
              <p className="text-xs text-muted-foreground">Only affects total display. Line items keep their own currency.</p>
            </div>
          </div>

          {/* Items */}
          <div>
            <Label className="mb-2 block">Items</Label>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left w-10">#</th>
                    <th className="p-2 text-left">Description / النوع</th>
                    <th className="p-2 text-right w-16">Qty</th>
                    <th className="p-2 text-right w-28">Unit Price</th>
                    <th className="p-2 text-center w-20">Currency</th>
                    <th className="p-2 text-center w-14">Tax</th>
                    <th className="p-2 text-right w-28">Total</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => (
                    <tr key={idx} className="border-t relative">
                      <td className="p-2 text-muted-foreground">{idx + 1}</td>
                      <td className="p-2 relative">
                        <div className="flex items-center gap-1">
                          <Input
                            value={activeLineIdx === idx ? itemSearch : line.description}
                            onChange={(e) => {
                              setActiveLineIdx(idx);
                              setItemSearch(e.target.value);
                              updateLine(idx, 'description', e.target.value);
                            }}
                            onFocus={() => setActiveLineIdx(idx)}
                            onBlur={() => setTimeout(() => { setActiveLineIdx(null); setItemResults([]); }, 200)}
                            placeholder="Search item or type description..."
                            className="h-8 flex-1"
                            data-testid={`faux-line-desc-${idx}`}
                          />
                          <button onClick={() => { setQaItemMode('add'); setQaItemEdit(null); setQaItemOpen(true); }}
                            className="h-8 w-8 flex items-center justify-center rounded-md border border-dashed border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10 shrink-0"
                            title="Add New Item" data-testid={`faux-add-item-btn-${idx}`}>
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {activeLineIdx === idx && itemResults.length > 0 && (
                          <div className="absolute z-50 left-0 right-0 bg-background border rounded shadow-lg max-h-48 overflow-auto top-full">
                            {itemResults.map(item => (
                              <div key={item.id} className="px-3 py-1.5 hover:bg-muted cursor-pointer text-xs flex justify-between items-center"
                                onMouseDown={() => selectItem(item, idx)}>
                                <span className="truncate">{item.name}</span>
                                <div className="flex items-center gap-1 ml-2">
                                  <span className="text-muted-foreground whitespace-nowrap">{item.price?.toLocaleString()}</span>
                                  <button className="text-blue-500 hover:text-blue-700 p-0.5" title="Edit"
                                    onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); setQaItemMode('edit'); setQaItemEdit(item); setQaItemOpen(true); }}>
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {activeLineIdx === idx && itemResults.length === 0 && itemSearch.trim().length >= 2 && (
                          <div className="absolute z-50 left-0 right-0 bg-background border rounded shadow-lg top-full p-2">
                            <p className="text-xs text-muted-foreground">No items found</p>
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        <Input type="number" min="1" step="1" value={line.quantity}
                          onChange={(e) => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          className="h-8 text-right" data-testid={`faux-line-qty-${idx}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" min="0" step="0.01" value={line.unit_price}
                          onChange={(e) => updateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="h-8 text-right" data-testid={`faux-line-price-${idx}`} />
                      </td>
                      <td className="p-2">
                        <select 
                          value={line.currency || 'LBP'}
                          onChange={(e) => updateLine(idx, 'currency', e.target.value)}
                          className="h-8 w-full rounded border border-input bg-background px-2 text-sm"
                          data-testid={`faux-line-currency-${idx}`}
                        >
                          <option value="LBP">LBP</option>
                          <option value="USD">USD</option>
                        </select>
                      </td>
                      <td className="p-2 text-center">
                        <input 
                          type="checkbox" 
                          checked={line.is_taxed || false}
                          onChange={(e) => updateLine(idx, 'is_taxed', e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300"
                          title="Item is taxed (TTC)"
                          data-testid={`faux-line-taxed-${idx}`}
                        />
                      </td>
                      <td className="p-2 text-right font-mono text-sm">
                        {line.currency === 'USD' 
                          ? (line.quantity * line.unit_price).toFixed(2)
                          : Math.round(line.quantity * line.unit_price).toLocaleString()
                        } {line.currency || 'LBP'}
                      </td>
                      <td className="p-2">
                        {lines.length > 1 && (
                          <button onClick={() => removeLine(idx)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t">
                    <td colSpan="8" className="p-2">
                      <Button variant="outline" size="sm" onClick={addLine} data-testid="faux-add-line">
                        <Plus className="w-3 h-3 mr-1" /> Add Line
                      </Button>
                    </td>
                  </tr>
                  <tr className="border-t bg-muted/30">
                    <td colSpan="6" className="p-3 text-right font-bold">TTC / المجموع</td>
                    <td className="p-3 text-right font-bold font-mono text-lg" data-testid="faux-total">
                      {currency === 'USD' ? total.toFixed(2) : Math.round(total).toLocaleString()} {currency}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1">
            <Label>Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional notes..." data-testid="faux-notes" />
          </div>

          {/* Save */}
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="faux-save-btn">
              {saving ? 'Saving...' : editingId ? 'Update & Print' : 'Create & Print'}
            </Button>
            <Button variant="outline" onClick={() => { setView('list'); resetForm(); }}>Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  // ===================== LIST VIEW =====================
  return (
    <div className="p-4 lg:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="faux-invoices-title">Faux Invoices</h1>
          <p className="text-sm text-muted-foreground">Draft documents - no inventory or financial impact</p>
        </div>
        <Button onClick={() => setView('create')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="faux-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Faux Invoice
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)}
            placeholder="Search by customer or serial..." className="pl-10" data-testid="faux-filter-search" />
        </div>
        <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}
          className="w-40" data-testid="faux-filter-from" />
        <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}
          className="w-40" data-testid="faux-filter-to" />
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-auto">
        <table className="w-full text-sm" data-testid="faux-invoices-table">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 text-left font-medium">Serial #</th>
              <th className="p-3 text-left font-medium">Date</th>
              <th className="p-3 text-left font-medium">Customer</th>
              <th className="p-3 text-right font-medium">Items</th>
              <th className="p-3 text-right font-medium">Total</th>
              <th className="p-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="p-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan="6" className="p-8 text-center text-muted-foreground">No faux invoices found</td></tr>
            ) : invoices.map(inv => (
              <tr key={inv.id} className="border-t hover:bg-muted/20 cursor-pointer" onClick={() => handlePrint(inv)}>
                <td className="p-3 font-mono font-medium">{inv.serial_number}</td>
                <td className="p-3">{inv.date}</td>
                <td className="p-3">{inv.customer_name}</td>
                <td className="p-3 text-right">{inv.items?.length || 0}</td>
                <td className="p-3 text-right font-mono">{Math.round(Number(inv.total)).toLocaleString()}</td>
                <td className="p-3 text-right">
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(inv)} data-testid={`faux-edit-${inv.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handlePrint(inv)} data-testid={`faux-print-${inv.id}`}>
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(inv.id)}
                      className="text-red-500 hover:text-red-700" data-testid={`faux-delete-${inv.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <QuickAddInventoryDialog open={qaItemOpen} onOpenChange={setQaItemOpen}
        mode={qaItemMode} editData={qaItemEdit}
        organizationId={currentOrg?.id} onSaved={(item) => {
          // After adding, refresh is not needed since faux invoice items are free text
        }} />
    </div>
  );
};

export default FauxInvoicesPage;
